import classnames from 'classnames';
import playerjs from 'player.js';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  updateShareVideoIframeLoaded,
  updateShareVideoPlayerReady,
} from 'actions/shareVideo';
import { TARGET_SHARE_PAGE_EMBED_PARAM } from 'constants/oEmbed';
import { EMBED_VIDEO, LOOM_URI } from 'constants/routes';

import { updateVideoPlayerHeight } from '../../actions/videoPlayer';

import EmbedVideo from 'components/embed-video';
import { headerHeight } from 'components/layout/header';
import { navBarAnimationDuration } from 'components/layout/navigation';

import './styles.less';

const DEFAULT_VIDEO_CONTAINER_PADDING_BOTTOM = 56.25; // 16:9
const TITLE_PADDING = 160;

// This components acts as a normal video compoennt for the main
// video on the share page video, but an iFrame for embeded if used
// in that manner
class VideoFrame extends Component {
  state = {
    styles: this.getResponsiveVideoWrapperDimensions(),
  };

  render() {
    const {
      EditVideoButtons,
      isMainVideoOnSharePage,
      startTime,
      theaterMode,
      videoId,
      ViewerVideoButtons,
    } = this.props;

    const { styles } = this.state;

    const params = startTime ? `&t=${startTime}` : '';

    // we add an extra param to signify that this video is the main
    // embed on this page. This is because with the loom sdk link expansion
    // loom links in comments etc will turn into embeds and they will try
    // to use the window.parent redux store. This param
    // TARGET_SHARE_PAGE_EMBED_PARAM signals the embedded page to not
    // use the parent store if this is not the main video for that page.
    const embedUrl = `${LOOM_URI}${EMBED_VIDEO}/${videoId}?${TARGET_SHARE_PAGE_EMBED_PARAM}=1${params}`;

    let contentContainer = null;

    if (isMainVideoOnSharePage) {
      contentContainer = (
        <EmbedVideo
          EditVideoButtons={EditVideoButtons}
          ViewerVideoButtons={ViewerVideoButtons}
          stylesFromShareVideoParent={{ position: 'absolute' }}
        />
      );
    } else {
      contentContainer = (
        <iframe
          className="embed-video-iframe"
          ref={iframe => (this.videoIframe = iframe)}
          style={styles.iframe}
          src={embedUrl}
          frameBorder="0"
          allowFullScreen
        />
      );
    }

    return (
      <div className="embed-video-frame-wrapper" style={styles.wrapper}>
        <div
          className={classnames('embed-video-frame-container', {
            'rounded-corners': !theaterMode,
          })}
          ref={container => (this.videoContainer = container)}
          style={styles.container}
        >
          {contentContainer}
        </div>
        {this.props.children}
      </div>
    );
  }

  // to know what's going on here, check this:
  // http://www.goldenapplewebdesign.com/responsive-aspect-ratios-with-pure-css/
  getResponsiveVideoWrapperDimensions() {
    const { videoProperties, iframeVisible, theaterMode } = this.props;
    const styles = { container: {}, wrapper: {}, iframe: {} };

    if (!videoProperties) {
      return styles;
    }

    const { width, height } = videoProperties || {};

    if (!width || !height || !this.videoContainer) {
      return styles;
    }

    const maxContainerWidth = this.videoContainer.offsetWidth;

    let paddingBottomValue = 0;

    if (!theaterMode) {
      const resultingHeight =
        (maxContainerWidth * DEFAULT_VIDEO_CONTAINER_PADDING_BOTTOM) / 100;

      // titlePadding for the video title, the padding above it, and a little space below it
      const maxHeight = window.innerHeight - headerHeight - TITLE_PADDING;
      const minHeight = 300;

      if (resultingHeight > maxHeight) {
        paddingBottomValue = Math.max(
          (maxHeight * 100) / maxContainerWidth,
          (minHeight * 100) / maxContainerWidth
        );
      } else {
        paddingBottomValue = DEFAULT_VIDEO_CONTAINER_PADDING_BOTTOM;
      }
    }

    // enforce minimum aspect ratio of 16:9 for super wide videos
    styles.container.paddingBottom = `${paddingBottomValue}%`;
    styles.wrapper.minHeight = 'auto';
    !iframeVisible && (styles.iframe.visibility = 'hidden');

    return styles;
  }

  updateStyles() {
    this.setState({
      styles: this.getResponsiveVideoWrapperDimensions(),
    });
  }

  componentDidMount() {
    const { isMainVideoOnSharePage } = this.props;

    if (isMainVideoOnSharePage) {
      this.updateStyles();

      this.onResize = () => {
        this.updateStyles();
      };

      window.addEventListener('resize', this.onResize);

      if (this.videoContainer) {
        this.props.updateVideoPlayerHeight(this.videoContainer.clientHeight);
      }
    } else {
      const player = playerjs.Player(this.videoIframe);

      player.on('ready', () => {
        this.props.updateShareVideoPlayerReady(true);
      });
      this.videoIframe.onload = () => {
        this.props.updateShareVideoIframeLoaded(true);
      };
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isNavigationCollapsed !== this.props.isNavigationCollapsed) {
      setTimeout(() => {
        // wait for collapse/uncollapse animation, + 50ms just in case
        this.updateStyles();
      }, navBarAnimationDuration + 50);
    } else if (prevProps.theaterMode !== this.props.theaterMode) {
      this.updateStyles();
    }

    if (
      this.videoContainer &&
      prevProps.videoFrameHeight !== this.videoContainer.clientHeight
    ) {
      this.props.updateVideoPlayerHeight(this.videoContainer.clientHeight);
    }
  }

  componentWillUnmount() {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
  }
}

const mapStateToProps = state => {
  const { video, iframe_visible: iframeVisible } = state.share_video;
  const {
    isMainVideoOnSharePage,
    theater_mode: theaterMode,
    videoFrameHeight,
  } = state.video_player;
  const isNavigationCollapsed = state?.layout?.isNavigationCollapsed;

  return {
    iframeVisible,
    isMainVideoOnSharePage,
    isNavigationCollapsed,
    theaterMode,
    videoProperties: (video || {}).video_properties,
    videoFrameHeight,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateShareVideoIframeLoaded: loaded =>
      dispatch(updateShareVideoIframeLoaded(loaded)),
    updateShareVideoPlayerReady: playerReady =>
      dispatch(updateShareVideoPlayerReady(playerReady)),
    updateVideoPlayerHeight: height =>
      dispatch(updateVideoPlayerHeight(height)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(VideoFrame);
